import cn from "classnames";
import Image from "next/image";
import { useTranslation } from "next-i18next";
import { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";

import Video from "@components/cms/Video/Video";
import {
  allowedVideoExtensions,
  allowedImageExtensions,
  allowedThreeDExtensions,
  VIDEO_MAX_SIZE,
  IMAGE_MAX_SIZE,
  THREE_D_MAX_SIZE,
  isVideoExtension,
  isThreeDExtension,
} from "@config/config";
import { useStore } from "@hooks/useZustandProvider";
import upoadingSvg from "@images/svg/upoading.svg";
import i from "@styles/icons.module.scss";
import ThreeDModel from "@ui-kit/ThreeDModel/ThreeDModel";
import { getFileExtension } from "@utils/utils";

import s from "./Dropzone.module.scss";
import {
  useImageUpload,
  useVideoUpload,
  useThreeDUpload,
} from "./hooks/useFileUpload";

export const defaultDropzoneConfig = {
  maxFiles: 1,
  accept: {
    "image/*": allowedImageExtensions.map((ext) => `.${ext}`),
    "video/*": allowedVideoExtensions.map((ext) => `.${ext}`),
    "application/octet-stream": allowedThreeDExtensions.map((ext) => `.${ext}`),
  },
};

const Dropzone = ({
  className,
  onDrop = () => {},
  setLoading = () => {},
  loading,
  preview,
  removeImage,
  placeholder,
  buttonText,
  config = {},
  size,
  imageProps = {},
  imageClassNames = "",
  unoptimized = false,
  useModal,
  allowVideo = false,
  allowImage = true,
  allowThreeD = false,
}) => {
  const { cms, modals, updateModals } = useStore();
  const { isEditMode } = cms.uiOptions;

  const { isOpenUploadImageModal } = modals;
  const { t } = useTranslation();
  const [images, setImages] = useState([]);

  const { uploadImage, loading: loadingImages } = useImageUpload();
  const { uploadVideoFile, loading: loadingVideo } = useVideoUpload();
  const { uploadThreeDFile, loading: loadingThreeDModel } = useThreeDUpload();

  const getAcceptConfig = () => {
    const accept = {};

    if (allowImage) {
      accept["image/*"] = allowedImageExtensions.map((ext) => `.${ext}`);
    }

    if (allowVideo) {
      accept["video/*"] = allowedVideoExtensions.map((ext) => `.${ext}`);
    }

    if (allowThreeD) {
      accept["application/octet-stream"] = allowedThreeDExtensions.map(
        (ext) => `.${ext}`,
      );
    }

    return accept;
  };

  const toggleModal = () => {
    if (isEditMode) {
      updateModals({
        isOpenUploadImageModal: !isOpenUploadImageModal,
      });
    }
  };

  const { open, getRootProps, getInputProps } = useDropzone({
    ...defaultDropzoneConfig,
    accept: getAcceptConfig(),
    onDropAccepted: async (acceptedFiles) => {
      try {
        const file = acceptedFiles[0];
        const isVideo = file?.type?.startsWith("video/");
        const isThreeD = file?.type?.startsWith("model/");
        const maxSize = isVideo
          ? VIDEO_MAX_SIZE
          : isThreeD
            ? THREE_D_MAX_SIZE
            : IMAGE_MAX_SIZE;

        if (file.size > maxSize) {
          return toast.error(
            `${file.name} ${isVideo ? "video" : "image"} size is ${(
              file.size /
              1024 /
              1024
            ).toFixed(2)} MB. The maximum size should not be more than ${(
              maxSize /
              1024 /
              1024
            ).toFixed(2)} MB`,
          );
        }

        setImages([
          {
            file: file,
            preview: URL.createObjectURL(file),
          },
        ]);
      } catch (error) {
        console.log("useDropzone error", error);
      }
    },
  });

  useEffect(() => {
    if (images.length) {
      const { file, preview } = images[0];
      const isVideo = file?.type?.startsWith("video/");
      const isThreeD = file?.type?.startsWith("model/");

      if (isThreeD && !allowThreeD) {
        toast.error("ThreeD model upload is not allowed");
        setImages([]);
        return;
      }

      if (isVideo && !allowVideo) {
        toast.error("Video upload is not allowed");
        setImages([]);
        return;
      }

      if (!isVideo && !allowImage) {
        toast.error("Image upload is not allowed");
        setImages([]);
        return;
      }

      const handleUpload = async () => {
        try {
          let result;
          if (isThreeD) {
            result = await uploadThreeDFile(file);
          } else if (isVideo) {
            result = await uploadVideoFile(file);
          } else {
            result = await uploadImage(file);
          }
          onDrop(result);
          setImages([]);
        } catch (error) {
          console.error("Upload error:", error);
          setImages([]);
        }
      };

      handleUpload();
    }
  }, [images]);

  const handleRemoveImage = (e) => {
    e.stopPropagation();
    removeImage();
  };

  useEffect(() => {
    setLoading(loadingImages || loadingVideo || loadingThreeDModel);
  }, [loadingImages, loadingVideo, loadingThreeDModel]);

  const renderPreview = () => {
    const extension = getFileExtension(preview);

    if (isVideoExtension(extension)) {
      return (
        <Video videoSrc={preview} className={cn(s.image, imageClassNames)} />
      );
    }

    if (isThreeDExtension(extension)) {
      return (
        <div
          className={cn(s.image, imageClassNames)}
          style={{ width: "100%", height: "100%" }}
        >
          <ThreeDModel
            modelSrc={preview}

            // modelSrc={
            //   "https://dev-luxexpress.fra1.cdn.digitaloceanspaces.com/media/3d_models/SUPERIOR-bus.glb"
            // }
          />
        </div>
      );
    }

    return (
      <Image
        className={cn(s.image, imageClassNames)}
        src={preview}
        alt=""
        width={0}
        height={0}
        sizes="100vw"
        style={{ width: "100%", height: "100%" }}
        unoptimized={unoptimized}
      />
    );
  };

  return (
    <div className={cn(s.wrapDropzone, !preview && s.empty, className)}>
      {useModal ? (
        // NOTE: useModal call modal with dropzone component where ThreeD, Video, and Image are allowed
        <div className={s.dropzone} onClick={toggleModal}>
          {preview ? (
            <div className={s.preview}>
              {size && <div className={s.size}>{size}</div>}
              {renderPreview()}
            </div>
          ) : (
            <div className={s.empty}>
              {size && <div className={s.size}>{size}</div>}
              <div className={s.text}>
                Click to open modal
                <br />
                to upload a picture and other information
              </div>
            </div>
          )}
        </div>
      ) : (
        <div {...getRootProps({ className: s.dropzone })}>
          <input {...getInputProps()} />
          {loadingImages || loadingVideo || loadingThreeDModel ? (
            <div className={s.loading}>
              <Image src={upoadingSvg} alt="" />
            </div>
          ) : preview ? (
            <div className={s.preview}>
              {size && <div className={s.size}>{size}</div>}
              <div className={s.actions}>
                <div className={cn(s.action, s.upload)} onClick={open}>
                  <icon className={i.download} />
                </div>
                <div className={cn(s.action, s.trash)}>
                  <icon className={i.trash} onClick={handleRemoveImage} />
                </div>
              </div>
              {renderPreview()}
            </div>
          ) : (
            <div className={s.empty}>
              {size && <div className={s.size}>{size}</div>}
              <icon className={cn(i.upload_v2, s.icon)} />
              <div className={s.text}>
                Drag&Drop
                <br />
                or click to <span>Browse</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dropzone;
